/**
 * Talent profile page — mockup §5495-5717.
 *
 * What lives here (talent-only surface):
 *   - Basics: display name + email (read-only from SSO), bio, GitHub
 *   - Directory profile: Entra-synced job/department/company/country/office
 *   - Skills & interests: the shared ProfileSkills editor (chips, catalog
 *     picker, a "Notify me" bell per skill)
 *   - Preferred FA dropdown (FA1-FA4; filters opportunity feed)
 *   - "Visible in directory" toggle (council search + invite gate)
 *
 * Email switches (weekly digest and the reminders) live in the shared
 * EmailSettings panel under every profile, not here.
 *
 * Split from the unified Profile.tsx so the talent surface can diverge
 * from the council shape without conditional gating. Sibling file is
 * CouncilProfile.tsx.
 */

import { useEffect, useState } from 'react'
import {
  Button,
  Caption1,
  Dropdown,
  Input,
  Option,
  Spinner,
  Subtitle2,
  Switch,
  Text,
  Textarea,
  Title2,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import {
  Building20Regular,
  Globe20Regular,
  Location20Regular,
  Organization20Regular,
  Save20Regular,
} from '@fluentui/react-icons'
import { useAuth } from '../../context/AuthContext'
import { useEffectiveUser } from '../../hooks/useEffectiveUser'
import { ProfileSkills } from '../../components/ProfileSkills'
import { useToast } from '../../components/ToastProvider'
import { useDocumentTitle } from '../../hooks/useDocumentTitle'
import { useUpdateProfile } from '../../hooks/useProfile'
import { FOCUS_AREAS } from '../../types/focus_area'
import { bayerColors } from '../../theme'
import { apiErrorMessage } from '../../lib/apiError'
import { githubHandleProblem } from '../../lib/githubHandle'
import { TitleWithHelp } from '../../components/TitleWithHelp'

// Talents pick a preferred FA from FA1-FA4. BO is council-only.
const TALENT_PREFERRED_FA_CODES = ['FA1', 'FA2', 'FA3', 'FA4'] as const

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    maxWidth: '1080px',
  },
  head: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalL,
    flexWrap: 'wrap',
  },
  headTitleBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '260px 1fr',
    gap: tokens.spacingHorizontalXL,
    '@media (max-width: 720px)': {
      gridTemplateColumns: '1fr',
    },
  },
  side: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalL,
    background: '#FFFFFF',
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow2,
  },
  avatar: {
    width: '88px',
    height: '88px',
    borderRadius: '50%',
    background: bayerColors.navy,
    color: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '32px',
    fontWeight: 600,
    letterSpacing: '-0.01em',
  },
  visibilityRow: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalM,
    paddingTop: tokens.spacingVerticalM,
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke2,
  },
  main: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  section: {
    background: '#FFFFFF',
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow2,
    padding: tokens.spacingHorizontalL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  sectionHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  fieldLabel: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: 600,
    color: tokens.colorNeutralForeground2,
  },
  fieldHint: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },
  readonlyInput: {
    background: tokens.colorNeutralBackground3,
    cursor: 'not-allowed',
  },
  dirRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 0',
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke2,
  },
  dirRowLast: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 0',
  },
  dirIcon: {
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  dirContent: { display: 'flex', flexDirection: 'column', gap: '1px' },
  dirLabel: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
  },
  dirValue: { fontSize: '14px', color: tokens.colorNeutralForeground1 },
  dirEmpty: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic' as const,
  },
})

function initials(name: string | null | undefined) {
  if (!name) return '?'
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => (p[0] ?? '').toUpperCase())
    .join('')
}

export function TalentProfile() {
  useDocumentTitle('My profile')
  const styles = useStyles()
  const { user } = useAuth()
  const effectiveUser = useEffectiveUser()
  const { showToast } = useToast()

  // ── Basics: bio + github (editable; name+email come from SSO) ──────────
  // Source from effectiveUser so impersonation shows the impersonated
  // user's draft + dirty state, not the real admin's.
  const [bioDraft, setBioDraft] = useState<string>(effectiveUser?.bio ?? '')
  const [githubDraft, setGithubDraft] = useState<string>(effectiveUser?.github_handle ?? '')
  const updateProfile = useUpdateProfile()
  // Reset drafts when the impersonated user changes mid-session so the
  // form doesn't keep the previous identity's bio/github locked in.
  useEffect(() => {
    setBioDraft(effectiveUser?.bio ?? '')
    setGithubDraft(effectiveUser?.github_handle ?? '')
  }, [effectiveUser?.id, effectiveUser?.bio, effectiveUser?.github_handle])
  const bioDirty = (effectiveUser?.bio ?? '') !== bioDraft.trim()
  const githubDirty = (effectiveUser?.github_handle ?? '') !== githubDraft.trim()
  const anyDirty = bioDirty || githubDirty
  const githubProblem = githubHandleProblem(githubDraft)

  const saveBasics = () => {
    if (!anyDirty || githubProblem) return
    const patch: { bio?: string | null; github_handle?: string | null } = {}
    if (bioDirty) patch.bio = bioDraft.trim() || null
    if (githubDirty) patch.github_handle = githubDraft.trim() || null
    updateProfile.mutate(patch, {
      onSuccess: () => showToast({ title: 'Profile saved', intent: 'success' }),
      onError: (err) =>
        showToast({ title: 'Could not save profile', body: apiErrorMessage(err), intent: 'error' }),
    })
  }

  // ── Preferred focus areas ───────────────────────────────────────────────
  // Edited locally while the list is open and saved once when it closes.
  // Saving on every click built each request on a value the server had not
  // returned yet, so a quick second click overwrote the first ("I ticked it
  // and it didn't stick"). The draft is dropped once the save lands.
  const faSaved = effectiveUser?.preferred_focus_areas ?? []
  const [faDraft, setFaDraft] = useState<string[] | null>(null)
  const [faNeedsOne, setFaNeedsOne] = useState(false)
  const saveFocusAreas = useUpdateProfile()
  const faShown = faDraft ?? faSaved
  const commitFocusAreas = () => {
    if (faDraft === null) return
    const next = faDraft
    if (next.length === faSaved.length && next.every((c) => faSaved.includes(c))) {
      setFaDraft(null)
      return
    }
    saveFocusAreas.mutate(
      { preferred_focus_areas: next },
      {
        // Clear only the draft this save carried: a newer one is still the
        // user's, and its own save follows when the list closes again.
        onSuccess: () => {
          setFaDraft((d) => (d === next ? null : d))
          showToast({ title: 'Preferred FAs updated', intent: 'success' })
        },
        onError: (err) => {
          setFaDraft((d) => (d === next ? null : d))
          showToast({
            title: 'Could not update preferred FAs',
            body: apiErrorMessage(err),
            intent: 'error',
          })
        },
      },
    )
  }

  // ── Visibility (persisted immediately on toggle) ────────────────────────
  const isImpersonating = effectiveUser?.id !== user?.id
  const [visibleDraft, setVisibleDraft] = useState(
    effectiveUser?.visible_in_directory ?? true,
  )
  useEffect(() => {
    setVisibleDraft(effectiveUser?.visible_in_directory ?? true)
  }, [effectiveUser?.id, effectiveUser?.visible_in_directory])

  const handleVisibilityToggle = (_: unknown, d: { checked: boolean }) => {
    setVisibleDraft(d.checked)
    updateProfile.mutate(
      { visible_in_directory: d.checked },
      {
        onError: (err) => {
          setVisibleDraft(!d.checked)
          showToast({
            title: 'Could not update visibility',
            body: apiErrorMessage(err),
            intent: 'error',
          })
        },
      },
    )
  }

  // ── Talent-profile activation (RBAC R1b) — persisted immediately ──────────
  const [activeDraft, setActiveDraft] = useState(
    effectiveUser?.talent_profile_active ?? false,
  )
  useEffect(() => {
    setActiveDraft(effectiveUser?.talent_profile_active ?? false)
  }, [effectiveUser?.id, effectiveUser?.talent_profile_active])

  const handleActiveToggle = (_: unknown, d: { checked: boolean }) => {
    setActiveDraft(d.checked)
    updateProfile.mutate(
      { talent_profile_active: d.checked },
      {
        onError: (err) => {
          setActiveDraft(!d.checked)
          showToast({
            title: 'Could not update talent profile',
            body: apiErrorMessage(err),
            intent: 'error',
          })
        },
      },
    )
  }

  return (
    <div className={styles.root}>
      <header className={styles.head}>
        <div className={styles.headTitleBlock}>
          <TitleWithHelp topic="profile">
            <Title2 as="h1">My profile</Title2>
          </TitleWithHelp>
        </div>
      </header>

      <div className={styles.grid}>
        {/* Side: identity + visibility */}
        <aside className={styles.side} aria-label="Identity">
          <div className={styles.avatar} aria-hidden="true">
            {initials(effectiveUser?.name)}
          </div>
          <Subtitle2 as="h2" style={{ textAlign: 'center' }}>
            {effectiveUser?.name ?? '—'}
          </Subtitle2>
          <Caption1 style={{ color: tokens.colorNeutralForeground3, textAlign: 'center' }}>
            {effectiveUser?.email ?? ''}
          </Caption1>

          {/* Preferred FA multi-select — talent picks any of FA1-4, used to
              filter/prioritize the opportunity feed. Freely editable (#781). */}
          <div style={{ width: '100%' }}>
            <Caption1
              style={{
                display: 'block',
                color: tokens.colorNeutralForeground3,
                marginBottom: tokens.spacingVerticalXXS,
              }}
            >
              Preferred focus areas
            </Caption1>
            <Dropdown
              aria-label="Preferred focus areas"
              multiselect
              placeholder="Pick one or more"
              value={faShown.join(', ')}
              selectedOptions={faShown}
              onOptionSelect={(_, data) => {
                // An empty list reads as "every focus area" on the server, so
                // unticking the last one would do the opposite of what it says.
                if (data.selectedOptions.length === 0) {
                  setFaNeedsOne(true)
                  return
                }
                setFaNeedsOne(false)
                setFaDraft(data.selectedOptions)
              }}
              onOpenChange={(_, data) => {
                if (!data.open) commitFocusAreas()
              }}
              style={{ width: '100%' }}
              // One save at a time: two in flight can land out of order.
              disabled={saveFocusAreas.isPending}
              data-testid="preferred-fa-dropdown"
            >
              {FOCUS_AREAS.filter((f) =>
                TALENT_PREFERRED_FA_CODES.includes(
                  f.code as (typeof TALENT_PREFERRED_FA_CODES)[number],
                ),
              ).map((f) => (
                <Option key={f.code} value={f.code} text={`${f.code} — ${f.name}`}>
                  {`${f.code} — ${f.name}`}
                </Option>
              ))}
            </Dropdown>
            <Caption1
              style={{
                display: 'block',
                color: tokens.colorNeutralForeground3,
                marginTop: tokens.spacingVerticalXXS,
                fontSize: tokens.fontSizeBase200,
              }}
            >
              {faNeedsOne
                ? 'Keep at least one. To hear about none, turn off Available as talent.'
                : 'Used to filter and prioritize opportunities.'}
            </Caption1>
          </div>

          <div className={styles.visibilityRow}>
            <div>
              <div style={{ fontWeight: 600, fontSize: tokens.fontSizeBase300 }}>
                Available as talent
              </div>
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                Lets you apply, appear in the Talent directory, and hear about positions.
              </Caption1>
            </div>
            <Switch
              aria-label="Available as talent"
              checked={activeDraft}
              onChange={handleActiveToggle}
            />
          </div>
          <div className={styles.visibilityRow}>
            <div>
              <div style={{ fontWeight: 600, fontSize: tokens.fontSizeBase300 }}>
                Visible in directory
              </div>
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                Council can find and invite you to demands.
              </Caption1>
            </div>
            <Switch
              aria-label="Visible in directory"
              checked={visibleDraft}
              onChange={handleVisibilityToggle}
            />
          </div>
          {isImpersonating && (
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
              Changes apply to the impersonated user&apos;s profile.
            </Caption1>
          )}
        </aside>

        {/* Main column */}
        <div className={styles.main}>
          {/* Basics */}
          <section className={styles.section} aria-labelledby="profile-basics-h">
            <div className={styles.sectionHeader}>
              <Subtitle2 id="profile-basics-h" as="h2">
                Basics
              </Subtitle2>
            </div>

            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor="profile-name">
                Display name
              </label>
              <Input
                id="profile-name"
                value={effectiveUser?.name ?? ''}
                readOnly
                input={{ className: styles.readonlyInput }}
                aria-label="Display name"
              />
            </div>

            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor="profile-email">
                Work email
              </label>
              <Input
                id="profile-email"
                value={effectiveUser?.email ?? ''}
                readOnly
                input={{ className: styles.readonlyInput }}
                aria-label="Work email"
              />
              <span className={styles.fieldHint}>
                Name and email come from Bayer SSO.
              </span>
            </div>

            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor="profile-bio">
                About
              </label>
              <Textarea
                id="profile-bio"
                value={bioDraft}
                onChange={(_, d) => setBioDraft(d.value)}
                rows={3}
                resize="vertical"
                placeholder="What you work on, in a sentence or two"
                aria-label="About"
              />
            </div>

            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor="profile-github">
                GitHub
              </label>
              <Input
                id="profile-github"
                value={githubDraft}
                onChange={(_, d) => setGithubDraft(d.value)}
                placeholder="your-username"
                aria-label="GitHub handle"
                contentBefore={
                  <span style={{ color: tokens.colorNeutralForeground3 }}>@</span>
                }
                aria-invalid={!!githubProblem}
                aria-describedby={githubProblem ? 'profile-github-problem' : undefined}
              />
              {githubProblem && (
                <span
                  id="profile-github-problem"
                  style={{ color: tokens.colorPaletteRedForeground1, fontSize: tokens.fontSizeBase200 }}
                >
                  {githubProblem}
                </span>
              )}
            </div>

            <div>
              <Button
                appearance="primary"
                icon={updateProfile.isPending ? <Spinner size="tiny" /> : <Save20Regular />}
                onClick={saveBasics}
                disabled={!anyDirty || !!githubProblem || updateProfile.isPending}
              >
                {updateProfile.isPending ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </section>

          {/* Directory profile (read-only from MS Entra) */}
          <section className={styles.section} aria-labelledby="profile-dir-h">
            <div className={styles.sectionHeader}>
              <Subtitle2 id="profile-dir-h" as="h2">
                Directory profile
              </Subtitle2>
              <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                From Microsoft Entra; IT can change it.
              </Text>
            </div>

            {[
              { icon: <Organization20Regular />, label: 'Job title', value: effectiveUser?.job_title },
              { icon: <Building20Regular />, label: 'Department', value: effectiveUser?.department },
              { icon: <Building20Regular />, label: 'Company', value: effectiveUser?.company },
              { icon: <Globe20Regular />, label: 'Country', value: effectiveUser?.country },
              { icon: <Location20Regular />, label: 'Office', value: effectiveUser?.office_location },
            ].map((f, i, arr) => (
              <div
                key={f.label}
                className={i < arr.length - 1 ? styles.dirRow : styles.dirRowLast}
              >
                <span className={styles.dirIcon}>{f.icon}</span>
                <div className={styles.dirContent}>
                  <Caption1 className={styles.dirLabel}>{f.label}</Caption1>
                  {f.value ? (
                    <Text className={styles.dirValue}>{f.value}</Text>
                  ) : (
                    <Text className={styles.dirEmpty}>Not set in directory</Text>
                  )}
                </div>
              </div>
            ))}
          </section>

          {/* Skills */}
          <section className={styles.section} aria-labelledby="profile-skills-h">
            <div className={styles.sectionHeader}>
              <Subtitle2 id="profile-skills-h" as="h2">
                Skills &amp; interests
              </Subtitle2>
            </div>

            <ProfileSkills />
          </section>
        </div>
      </div>

    </div>
  )
}
